import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Avatar } from "@/components/Avatar";
import { api, formatError, getToken, wsUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, Copy, LogOut, Users, ArrowLeft, Lock, Video, MapPin, Navigation, Image as ImageIcon, Search, X, ChevronUp, VenetianMask, Crown, Hourglass } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import VideoCall from "@/components/VideoCall";
import MessageMenu from "@/components/MessageMenu";
import ProfileCard from "@/components/ProfileCard";
import NearbySheet from "@/components/NearbySheet";
import MessageReactions from "@/components/MessageReactions";
import MessageImage from "@/components/MessageImage";
import MaskSheet from "@/components/MaskSheet";
import { shareLocation, distanceKm, formatDistance, geoSupported } from "@/lib/geo";

export default function ChatRoom() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [typingUsers, setTypingUsers] = useState({}); // user_id -> username
  const [input, setInput] = useState("");
  const [connected, setConnected] = useState(false);
  const [videoActive, setVideoActive] = useState(false);
  const [videoPeersCount, setVideoPeersCount] = useState(0);
  const [blocked, setBlocked] = useState(() => new Set());
  const [profileUser, setProfileUser] = useState(null); // {id, username, avatar, avatar_gradient, ...} when open
  const [myGeo, setMyGeo] = useState(() => (user?.geo ? { lat: user.geo.lat, lng: user.geo.lng } : null));
  const [geoError, setGeoError] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);
  const [nearbyOpen, setNearbyOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searchBusy, setSearchBusy] = useState(false);
  const [olderBusy, setOlderBusy] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [maskOpen, setMaskOpen] = useState(false);
  const [myMask, setMyMask] = useState({ enabled: false });
  const [incomingCall, setIncomingCall] = useState(null); // { username, startedAt }
  const ringAudioCtxRef = useRef(null);
  const ringIntervalRef = useRef(null);

  const isGeoRoom = room?.mode === "geo";
  const isDownLow = (room?.name || "").toLowerCase() === "downlow";
  const isMini = room?.type === "mini";
  const isDm = room?.type === "dm";
  // Video-capable rooms: mini rooms always, plus any public room that has been
  // tagged with video=true OR a known event room (e.g. Pop The Balloon: Philly Edition).
  const allowVideo = isMini || !!room?.video_enabled || (room?.name || "").toLowerCase().startsWith("pop the balloon");
  const allowImages = isMini || isDm;

  const fileInputRef = useRef(null);

  const shareMyLocation = useCallback(async () => {
    setGeoLoading(true);
    setGeoError("");
    try {
      const loc = await shareLocation();
      setMyGeo(loc);
    } catch (e) {
      setGeoError(e?.message || "Couldn't get your location");
    } finally {
      setGeoLoading(false);
    }
  }, []);

  // Auto-ask for location the first time a user enters a geo room
  useEffect(() => {
    if (isGeoRoom && !myGeo && !geoError && geoSupported()) {
      shareMyLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGeoRoom]);

  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const extraHandlersRef = useRef(new Set()); // VideoCall attaches here

  const registerHandler = useCallback((fn) => {
    extraHandlersRef.current.add(fn);
    return () => extraHandlersRef.current.delete(fn);
  }, []);

  // ——— Incoming video-call ringtone (WebAudio synth — zero network cost, no assets)
  const playRingTone = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!ringAudioCtxRef.current) ringAudioCtxRef.current = new Ctx();
      const ctx = ringAudioCtxRef.current;
      // Resume (autoplay policy) — safe to call repeatedly
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      // A soft two-tone "ding-ding"
      [
        { t: now, freq: 988 },          // B5
        { t: now + 0.22, freq: 1319 },  // E6
      ].forEach(({ t, freq }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, t);
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.42);
      });
    } catch {}
  }, []);

  const stopRinging = useCallback(() => {
    if (ringIntervalRef.current) {
      clearInterval(ringIntervalRef.current);
      ringIntervalRef.current = null;
    }
  }, []);

  useEffect(() => () => stopRinging(), [stopRinging]);

  // If I'm now in the call, make sure I'm not still ringing
  useEffect(() => {
    if (videoActive) { setIncomingCall(null); stopRinging(); }
  }, [videoActive, stopRinging]);

  // Fetch room metadata + message history
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [r, m, b] = await Promise.all([
          api.get(`/rooms/${id}`),
          api.get(`/rooms/${id}/messages`, { params: { limit: 50 } }),
          api.get(`/users/blocked`),
        ]);
        if (!active) return;
        setRoom(r.data);
        setMessages(m.data);
        setHasMore((m.data || []).length >= 50);
        setOnlineCount(r.data.online_count || 0);
        setBlocked(new Set((b.data || []).map((u) => u.id)));
        setMyMask(r.data.my_mask || { enabled: false });
      } catch (err) {
        toast.error(formatError(err));
        nav("/lobby");
      }
    })();
    return () => { active = false; };
  }, [id, nav]);

  // WebSocket
  useEffect(() => {
    if (!room) return;
    const token = getToken();
    const ws = new WebSocket(wsUrl(id, token));
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);

    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "message") {
          // client-side drop of messages from blocked users
          if (blocked.has(data.message.user_id)) return;
          setMessages((prev) => [...prev, data.message]);
        } else if (data.type === "presence") {
          setOnlineCount(data.online_count || 0);
          if (data.event === "join" && data.user?.id !== user.id) {
            toast(`${data.user.username} joined`, { icon: "✨" });
          } else if (data.event === "leave" && data.user_id !== user.id) {
            toast(`${data.username} left`, { icon: "–" });
          }
        } else if (data.type === "typing") {
          setTypingUsers((prev) => {
            const next = { ...prev };
            if (data.is_typing) next[data.user_id] = data.username;
            else delete next[data.user_id];
            return next;
          });
          // auto-clear typing after 4s in case leave not sent
          setTimeout(() => {
            setTypingUsers((prev) => {
              const next = { ...prev };
              delete next[data.user_id];
              return next;
            });
          }, 4000);
        } else if (data.type === "video-join" && data.user_id !== user.id) {
          setVideoPeersCount((c) => c + 1);
          // Ring + show incoming banner if *I'm* not currently in the call
          if (!videoActive) {
            setIncomingCall({ username: data.username, at: Date.now() });
            playRingTone();
            stopRinging();
            ringIntervalRef.current = setInterval(playRingTone, 3200);
            // Auto-stop after 30s of ringing so it doesn't get annoying
            setTimeout(() => stopRinging(), 30000);
          } else {
            toast(`${data.username} joined the call`, { icon: "🎥" });
          }
        } else if (data.type === "video-leave" && data.user_id !== user.id) {
          setVideoPeersCount((c) => {
            const next = Math.max(0, c - 1);
            if (next === 0) {
              setIncomingCall(null);
              stopRinging();
            }
            return next;
          });
        } else if (data.type === "reaction-update") {
          setMessages((prev) => prev.map((m) =>
            m.id === data.message_id ? { ...m, reactions: data.reactions } : m
          ));
        } else if (data.type === "message-deleted") {
          setMessages((prev) => prev.filter((m) => m.id !== data.message_id));
        }
        // Relay any message to VideoCall if mounted
        extraHandlersRef.current.forEach((h) => { try { h(data); } catch (e) { console.error(e); } });
      } catch {}
    };

    return () => { try { ws.close(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.id]);

  // Auto-scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typingUsers]);

  const send = (e) => {
    e?.preventDefault?.();
    const content = input.trim();
    if (!content || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "message", content }));
    setInput("");
    sendTyping(false);
  };

  const sendTyping = (isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "typing", is_typing: isTyping }));
    }
  };

  const onInputChange = (e) => {
    setInput(e.target.value);
    sendTyping(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => sendTyping(false), 1500);
  };

  const leaveRoom = async () => {
    try {
      if (room.type === "mini") await api.post(`/rooms/${id}/leave`);
    } catch {}
    nav("/lobby");
  };

  const copyInvite = () => {
    if (!room?.invite_code) return;
    const url = `${window.location.origin}/invite/${room.invite_code}`;
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied — paste anywhere");
  };

  const reactToMessage = (messageId, emoji) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "react", message_id: messageId, emoji }));
    } else {
      // Fallback to REST if socket dead
      api.post(`/messages/${messageId}/react`, { emoji }).catch((err) => toast.error(formatError(err)));
    }
  };

  const acceptIncomingCall = () => {
    setIncomingCall(null);
    stopRinging();
    setVideoActive(true);
  };

  const dismissIncomingCall = () => {
    setIncomingCall(null);
    stopRinging();
  };

  const onPickImage = () => fileInputRef.current?.click();

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!allowImages) {
      toast.error("Images can only be shared in mini rooms or DMs");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB)");
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/rooms/${id}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "message", kind: "image", image_path: data.image_path,
        }));
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploadingImage(false);
    }
  };

  const loadOlder = async () => {
    if (olderBusy || !hasMore || messages.length === 0) return;
    setOlderBusy(true);
    try {
      const oldest = messages[0];
      const { data } = await api.get(`/rooms/${id}/messages`, {
        params: { before: oldest.created_at, limit: 50 },
      });
      if ((data || []).length < 50) setHasMore(false);
      if ((data || []).length > 0) {
        setMessages((prev) => [...data, ...prev]);
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setOlderBusy(false);
    }
  };

  const runSearch = async (e) => {
    e?.preventDefault?.();
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults(null);
      return;
    }
    setSearchBusy(true);
    try {
      const { data } = await api.get(`/rooms/${id}/messages`, {
        params: { q, limit: 100 },
      });
      setSearchResults(data || []);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSearchBusy(false);
    }
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults(null);
  };

  if (!room) {
    return (
      <div className="min-h-screen grain">
        <Header />
        <div className="max-w-7xl mx-auto p-8 text-zinc-500">Loading room…</div>
      </div>
    );
  }

  const typingList = Object.entries(typingUsers).filter(([uid]) => uid !== user.id).map(([, name]) => name);

  return (
    <div className="min-h-screen flex flex-col grain">
      <Header />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-12 gap-4 min-h-0">
        {/* Sidebar */}
        <aside className="hidden lg:flex col-span-3 flex-col rounded-3xl border border-white/5 bg-[#0c0c0f] p-5 min-h-0">
          <button onClick={() => nav("/lobby")} data-testid="back-to-lobby" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-1 mb-4">
            <ArrowLeft className="w-3 h-3" /> Back to lobby
          </button>
          <div className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-400 font-bold">
            {isDm ? "DM · 1:1" : room.type === "mini" ? "Mini · Max 4" : room.topic || "Worldwide"}
          </div>
          <h2 className="font-display font-black text-2xl tracking-tight mt-2">{room.name}</h2>
          {room.description && <p className="text-sm text-zinc-400 mt-2">{room.description}</p>}

          {room.type === "mini" && room.invite_code && (
            <button onClick={copyInvite} data-testid="copy-invite-sidebar" className="mt-4 w-full rounded-xl bg-fuchsia-500/5 border border-fuchsia-500/20 p-3 text-left hover:bg-fuchsia-500/10 transition">
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300">Share invite link</div>
              <div className="flex items-center justify-between mt-1">
                <span className="font-display text-lg font-bold tracking-[0.15em]">{room.invite_code}</span>
                <Copy className="w-4 h-4 text-zinc-400" />
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 truncate">Click to copy /invite/{room.invite_code}</div>
            </button>
          )}

          <div className="mt-6 flex items-center justify-between text-xs text-zinc-400">
            <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> In the room</span>
            <span className="flex items-center gap-1.5 text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {onlineCount} online
            </span>
          </div>

          {room.type === "mini" && !isDm && (
            <div className="mt-3 grid grid-cols-4 gap-2">
              {Array.from({ length: 4 }).map((_, i) => {
                const m = room.members_detail?.[i];
                return m ? (
                  <div key={i} className="flex flex-col items-center gap-1">
                    <Avatar user={m} size={40} />
                    <span className="text-[10px] text-zinc-500 truncate max-w-full">{m.username}</span>
                  </div>
                ) : (
                  <div key={i} className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full border border-dashed border-white/10 flex items-center justify-center text-zinc-600">
                      <Lock className="w-3 h-3" />
                    </div>
                    <span className="text-[10px] text-zinc-700">empty</span>
                  </div>
                );
              })}
            </div>
          )}

          {isDm && (
            <div className="mt-4 flex items-center gap-3 rounded-2xl bg-white/[0.02] border border-white/5 p-3">
              {(() => {
                const partner = (room.members_detail || []).find((m) => m.id !== user.id);
                if (!partner) return <span className="text-xs text-zinc-500">Just you here yet.</span>;
                return (
                  <>
                    <Avatar user={partner} size={40} />
                    <div className="min-w-0">
                      <div className="text-sm font-display font-bold truncate">{partner.username}</div>
                      <div className="text-[10px] text-zinc-500">{partner.location || "private DM"}</div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {room.type === "public" && (
            <ScrollArea className="mt-3 flex-1 min-h-0">
              <div className="space-y-2 pr-2">
                {(room.members_detail || []).map((m) => (
                  <div key={m.id} className="flex items-center gap-2 text-sm">
                    <Avatar user={m} size={28} />
                    <span className="text-zinc-300">{m.username}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}

          {allowVideo && (
            <button
              onClick={() => setVideoActive(true)}
              data-testid="start-video-call-btn"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#e81cff] to-[#bd00ff] hover:from-[#ff4dff] hover:to-[#d000ff] text-white font-semibold py-2.5 shadow-[0_0_24px_rgba(232,28,255,0.35)] transition"
            >
              <Video className="w-4 h-4" /> Start video call
              {videoPeersCount > 0 && (
                <span className="ml-1 text-[10px] tracking-wider uppercase opacity-80">· {videoPeersCount} live</span>
              )}
            </button>
          )}

          {isDownLow && (
            <button
              onClick={() => setMaskOpen(true)}
              data-testid="go-incognito-btn"
              className={`mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl border py-2.5 font-semibold transition ${
                myMask?.enabled
                  ? "bg-fuchsia-500/10 border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/20"
                  : "bg-white/5 border-white/10 text-zinc-200 hover:bg-white/10"
              }`}
            >
              <VenetianMask className="w-4 h-4" />
              {myMask?.enabled ? `Incognito · ${myMask.alias}` : "Go incognito"}
            </button>
          )}

          <button onClick={leaveRoom} data-testid="leave-room-btn" className="mt-4 ghost-btn w-full inline-flex items-center justify-center gap-2">
            <LogOut className="w-4 h-4" /> Leave room
          </button>
        </aside>

        {/* Chat main */}
        <section className="col-span-12 lg:col-span-9 flex flex-col rounded-3xl border border-white/5 bg-[#0a0a0c] min-h-[70vh] overflow-hidden">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-white/5">
            <button onClick={() => nav("/lobby")} className="text-zinc-400 p-1" aria-label="Back"><ArrowLeft className="w-5 h-5" /></button>
            <div className="text-center min-w-0 px-2">
              <div className="font-display font-bold truncate">{room.name}</div>
              <div className="text-[10px] text-zinc-500">{onlineCount} online</div>
            </div>
            <div className="flex items-center gap-1">
              {allowVideo && !videoActive && (
                <button
                  onClick={() => setVideoActive(true)}
                  data-testid="mobile-start-video-btn"
                  aria-label="Start video call"
                  className="relative w-9 h-9 rounded-full bg-gradient-to-br from-[#e81cff] to-[#bd00ff] text-white flex items-center justify-center shadow-[0_0_16px_rgba(232,28,255,0.5)]"
                >
                  <Video className="w-4 h-4" />
                  {videoPeersCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-400 text-[9px] text-black font-bold flex items-center justify-center">
                      {videoPeersCount}
                    </span>
                  )}
                </button>
              )}
              {isDownLow && (
                <button
                  onClick={() => setMaskOpen(true)}
                  data-testid="mobile-mask-btn"
                  aria-label="Incognito mask"
                  className={`w-9 h-9 rounded-full flex items-center justify-center ${
                    myMask?.enabled ? "bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-500/40" : "text-zinc-400"
                  }`}
                >
                  <VenetianMask className="w-4 h-4" />
                </button>
              )}
              <button onClick={() => setSearchOpen((v) => !v)} className="text-zinc-400 p-1" data-testid="mobile-search-toggle" aria-label="Search"><Search className="w-5 h-5" /></button>
              <button onClick={leaveRoom} className="text-zinc-400 p-1" aria-label="Leave"><LogOut className="w-5 h-5" /></button>
            </div>
          </div>

          {/* Incoming video-call banner (pulses + rings until accepted/dismissed) */}
          {incomingCall && allowVideo && (
            <div
              data-testid="incoming-call-banner"
              className="relative overflow-hidden border-b border-fuchsia-500/30"
              style={{ background: "linear-gradient(90deg, rgba(232,28,255,0.12), rgba(189,0,255,0.18))" }}
            >
              <span aria-hidden className="absolute inset-0 pointer-events-none ring-pulse" />
              <div className="relative flex items-center gap-3 px-4 py-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#e81cff] to-[#bd00ff] text-white flex items-center justify-center shadow-[0_0_18px_rgba(232,28,255,0.55)]">
                  <Video className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-300 font-semibold">
                    Incoming video
                  </div>
                  <div className="text-sm truncate">
                    <span className="font-display font-bold">{incomingCall.username}</span>
                    <span className="text-zinc-400"> is on camera — want to join?</span>
                  </div>
                </div>
                <button
                  onClick={acceptIncomingCall}
                  data-testid="accept-video-call-btn"
                  className="text-xs px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold inline-flex items-center gap-1.5 transition shadow-[0_0_18px_rgba(52,211,153,0.45)]"
                >
                  <Video className="w-3.5 h-3.5" /> Join
                </button>
                <button
                  onClick={dismissIncomingCall}
                  data-testid="dismiss-video-call-btn"
                  aria-label="Dismiss"
                  className="text-zinc-400 hover:text-white p-2"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Mobile-only "Start video" banner for video-capable rooms (discoverable even without sidebar) */}
          {allowVideo && !videoActive && (
            <div className="lg:hidden px-4 py-2 border-b border-white/5 bg-[#09090b]">
              <button
                onClick={() => setVideoActive(true)}
                data-testid="mobile-start-video-banner"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#e81cff] to-[#bd00ff] text-white font-semibold py-2.5 text-sm shadow-[0_0_20px_rgba(232,28,255,0.35)]"
              >
                <Video className="w-4 h-4" />
                Start video call
                {videoPeersCount > 0 && (
                  <span className="ml-1 text-[10px] uppercase tracking-wider opacity-80">· {videoPeersCount} live</span>
                )}
              </button>
            </div>
          )}
          {room.type === "mini" && !videoActive && (
            <div className="lg:hidden px-4 py-2 border-b border-white/5 bg-[#09090b]">
              <button
                onClick={() => setVideoActive(true)}
                data-testid="mobile-start-video-banner"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#e81cff] to-[#bd00ff] text-white font-semibold py-2.5 text-sm shadow-[0_0_20px_rgba(232,28,255,0.35)]"
              >
                <Video className="w-4 h-4" />
                Start video call
                {videoPeersCount > 0 && (
                  <span className="ml-1 text-[10px] uppercase tracking-wider opacity-80">· {videoPeersCount} live</span>
                )}
              </button>
            </div>
          )}

          {/* Desktop room toolbar */}
          <div className="hidden lg:flex items-center justify-between px-6 py-3 border-b border-white/5 bg-[#09090b]">
            <div className="flex items-center gap-3 text-xs">
              <span className="font-display font-bold text-base text-white">{room.name}</span>
              <span className="text-zinc-500">·</span>
              <span className="text-zinc-400">{onlineCount} online</span>
              {isDownLow && myMask?.enabled && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-200">
                  <VenetianMask className="w-3 h-3" /> {myMask.alias}
                </span>
              )}
            </div>
            <button
              onClick={() => setSearchOpen((v) => !v)}
              data-testid="search-toggle-btn"
              className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-zinc-300 inline-flex items-center gap-1.5 transition"
            >
              <Search className="w-3.5 h-3.5" /> Search
            </button>
          </div>

          {/* Limited-time room countdown banner */}
          {room.expires_at && (
            <ExpiryBanner expiresAt={room.expires_at} />
          )}

          {/* Search bar */}
          {searchOpen && (
            <form onSubmit={runSearch} className="flex items-center gap-2 px-4 sm:px-6 py-3 border-b border-white/5 bg-[#0c0c0f]" data-testid="search-bar">
              <Search className="w-4 h-4 text-fuchsia-400" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search messages in this room…"
                className="text-input flex-1 py-2"
                data-testid="search-input"
                autoFocus
              />
              <button
                type="submit"
                disabled={searchBusy || !searchQuery.trim()}
                data-testid="search-submit-btn"
                className="text-xs px-4 py-2 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-semibold transition disabled:opacity-50"
              >
                {searchBusy ? "Searching…" : "Find"}
              </button>
              <button
                type="button"
                onClick={closeSearch}
                className="text-zinc-400 hover:text-white p-2"
                data-testid="search-close-btn"
                aria-label="Close search"
              >
                <X className="w-4 h-4" />
              </button>
            </form>
          )}

          {/* Who's here tonight — recent posters strip (public rooms only, when there's activity) */}
          {room.type === "public" && (room.recent_posters?.length || 0) > 0 && (
            <div className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-white/5 bg-[#09090b]" data-testid="recent-posters-strip">
              <span className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-400 font-semibold whitespace-nowrap">
                Here tonight
              </span>
              <div className="flex -space-x-2 overflow-hidden">
                {room.recent_posters.slice(0, 6).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setProfileUser(p)}
                    data-testid={`recent-poster-${p.id}`}
                    className="ring-2 ring-[#0a0a0c] rounded-full hover:ring-fuchsia-500/60 transition"
                    title={p.username}
                  >
                    <Avatar user={p} size={28} />
                  </button>
                ))}
              </div>
              <span className="text-xs text-zinc-500 truncate">
                {room.recent_posters[0].username}
                {room.recent_posters.length > 1 && ` & ${room.recent_posters.length - 1} more`}
              </span>
            </div>
          )}

          {/* Nearby room banner — GPS state / permission */}
          {isGeoRoom && (
            <div
              data-testid="nearby-banner"
              className={`flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-white/5 ${
                myGeo ? "bg-[#09090b]" : "bg-fuchsia-500/5"
              }`}
            >
              <Navigation className={`w-4 h-4 ${myGeo ? "text-fuchsia-400" : "text-fuchsia-300"}`} />
              {myGeo ? (
                <div className="flex-1 flex items-center justify-between gap-3">
                  <div className="text-xs text-zinc-400">
                    <span className="font-semibold text-white">Location shared</span> · distances shown next to each message.
                  </div>
                  <button
                    onClick={() => setNearbyOpen(true)}
                    data-testid="open-nearby-sheet-btn"
                    className="shrink-0 text-xs px-3 py-1.5 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-200 hover:bg-fuchsia-500/20 transition"
                  >
                    Who's near me?
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex-1 text-xs text-zinc-300">
                    {geoError ? (
                      <>Location blocked. Enable it in your browser settings to see who's near.</>
                    ) : (
                      <>Share your location to chat with people near you.</>
                    )}
                  </div>
                  <button
                    onClick={shareMyLocation}
                    disabled={geoLoading}
                    data-testid="share-location-btn"
                    className="text-xs px-3 py-1.5 rounded-full bg-[#e81cff] hover:bg-[#ff4dff] text-white font-semibold transition disabled:opacity-60"
                  >
                    {geoLoading ? "Finding you…" : "Share location"}
                  </button>
                </>
              )}
            </div>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-8 py-6 space-y-4" data-testid="messages-container">
            {searchResults !== null ? (
              <div className="text-xs text-zinc-500 mb-2 flex items-center justify-between">
                <span>{searchResults.length} match{searchResults.length === 1 ? "" : "es"} for "<span className="text-fuchsia-300">{searchQuery}</span>"</span>
                <button onClick={closeSearch} className="text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid="clear-search-btn">
                  <X className="w-3 h-3" /> clear
                </button>
              </div>
            ) : (
              hasMore && messages.length >= 20 && (
                <div className="flex justify-center">
                  <button
                    onClick={loadOlder}
                    disabled={olderBusy}
                    data-testid="load-older-btn"
                    className="text-xs px-4 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-zinc-300 inline-flex items-center gap-1.5 transition disabled:opacity-50"
                  >
                    <ChevronUp className="w-3 h-3" /> {olderBusy ? "Loading…" : "Load older"}
                  </button>
                </div>
              )
            )}
            {(searchResults ?? messages).length === 0 && (
              <div className="text-center text-zinc-500 mt-10 text-sm">
                {searchResults !== null ? "No matches in this room." : "Silence. Be the one who speaks first."}
              </div>
            )}
            {(searchResults ?? messages).map((m) => {
              const mine = m.user_id === user.id;
              const isMasked = !!m.masked;
              const authorSeed = {
                id: m.user_id,
                username: m.username,
                avatar: m.avatar,
                avatar_gradient: m.avatar_gradient,
                location: m.location,
                masked: isMasked,
              };
              const isImage = m.kind === "image" && m.image_path;
              return (
                <div key={m.id} className={`msg-enter flex ${mine ? "justify-end" : "justify-start"} gap-3 group`} data-testid={`message-${m.id}`}>
                  {!mine && (
                    <button
                      onClick={() => setProfileUser(authorSeed)}
                      data-testid={`msg-avatar-${m.id}`}
                      className="rounded-full hover:ring-2 hover:ring-fuchsia-500/60 transition"
                      aria-label={`Open ${m.username} profile`}
                    >
                      <Avatar user={authorSeed} size={36} />
                    </button>
                  )}
                  <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                    {!mine && (
                      <div className="flex items-center gap-1.5 mb-1 px-1 text-[11px]">
                        <button
                          onClick={() => setProfileUser(authorSeed)}
                          data-testid={`msg-username-${m.id}`}
                          className="text-zinc-300 font-semibold hover:text-white transition flex items-center gap-1"
                        >
                          {m.username}
                          {isMasked && <VenetianMask className="w-3 h-3 text-fuchsia-400" />}
                          {m.is_host && !isMasked && (
                            <Crown
                              className="w-3 h-3 text-amber-400"
                              data-testid={`host-crown-${m.id}`}
                              aria-label="Room host"
                            />
                          )}
                        </button>
                        {isGeoRoom && myGeo && m.geo ? (
                          <span className="inline-flex items-center gap-0.5 text-fuchsia-300" data-testid={`msg-distance-${m.id}`}>
                            <Navigation className="w-2.5 h-2.5" />
                            <span>{formatDistance(distanceKm(myGeo, m.geo))}</span>
                          </span>
                        ) : (
                          m.location && (
                            <span className="inline-flex items-center gap-0.5 text-zinc-500" data-testid={`msg-location-${m.id}`}>
                              <MapPin className="w-2.5 h-2.5 text-fuchsia-400/70" />
                              <span>{m.location}</span>
                            </span>
                          )
                        )}
                      </div>
                    )}
                    <div className="flex items-start gap-1">
                      {isImage ? (
                        <div className={mine ? "" : ""}>
                          <MessageImage path={m.image_path} />
                        </div>
                      ) : (
                        <div
                          className={
                            mine
                              ? "bg-gradient-to-br from-[#e81cff] to-[#bd00ff] text-white rounded-2xl rounded-tr-sm px-4 py-2.5 shadow-[0_4px_24px_rgba(232,28,255,0.25)]"
                              : "bg-[#1a1a20] border border-white/5 text-zinc-100 rounded-2xl rounded-tl-sm px-4 py-2.5"
                          }
                        >
                          <div className="whitespace-pre-wrap break-words text-sm">{m.content}</div>
                        </div>
                      )}
                      {!mine && (
                        <MessageMenu
                          message={m}
                          isHost={!!room.is_room_host}
                          roomId={id}
                          onMessageDeleted={(mid) => setMessages((prev) => prev.filter((x) => x.id !== mid))}
                          onUserBanned={(uid) => setMessages((prev) => prev.filter((x) => x.user_id !== uid))}
                          onBlocked={(blockedId) => {
                            setBlocked((prev) => new Set(prev).add(blockedId));
                            setMessages((prev) => prev.filter((x) => x.user_id !== blockedId));
                          }}
                        />
                      )}
                    </div>
                    <MessageReactions
                      message={m}
                      meId={user.id}
                      mine={mine}
                      onReact={(emoji) => reactToMessage(m.id, emoji)}
                    />
                  </div>
                </div>
              );
            })}
            {searchResults === null && typingList.length > 0 && (
              <div className="flex items-center gap-2 text-zinc-500 text-xs pl-1">
                <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-fuchsia-400" />
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-fuchsia-400" />
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-fuchsia-400" />
                </div>
                <span>{typingList.join(", ")} {typingList.length === 1 ? "is" : "are"} typing…</span>
              </div>
            )}
          </div>

          <form onSubmit={send} className="border-t border-white/5 p-4 sm:p-5 bg-[#0a0a0c]">
            <div className="flex items-center gap-2 sm:gap-3">
              {allowImages && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={onFileChosen}
                    className="hidden"
                    data-testid="image-file-input"
                  />
                  <button
                    type="button"
                    onClick={onPickImage}
                    disabled={!connected || uploadingImage}
                    data-testid="attach-image-btn"
                    className="shrink-0 w-11 h-11 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-zinc-300 inline-flex items-center justify-center transition disabled:opacity-50"
                    aria-label="Attach image"
                  >
                    {uploadingImage ? (
                      <span className="w-3 h-3 border-2 border-fuchsia-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <ImageIcon className="w-4 h-4" />
                    )}
                  </button>
                </>
              )}
              <input
                data-testid="message-input"
                value={input}
                onChange={onInputChange}
                placeholder={connected ? "Say something…" : "Connecting…"}
                className="text-input flex-1"
                disabled={!connected}
                maxLength={2000}
              />
              <button
                data-testid="send-message-btn"
                type="submit"
                disabled={!connected || !input.trim()}
                className="neon-btn flex items-center gap-2 px-4 sm:px-6 py-3"
              >
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">Send</span>
              </button>
            </div>
            {!connected && (
              <div className="text-[11px] text-zinc-500 mt-2">Reconnecting to Xhat…</div>
            )}
          </form>
        </section>
      </main>

      {videoActive && (
        <VideoCall
          roomId={id}
          me={user}
          wsRef={wsRef}
          registerHandler={registerHandler}
          members={room.members_detail || []}
          onLeave={() => setVideoActive(false)}
        />
      )}

      <ProfileCard
        open={!!profileUser}
        userId={profileUser?.id}
        seed={profileUser}
        onClose={() => setProfileUser(null)}
        onBlocked={(blockedId) => {
          setBlocked((prev) => new Set(prev).add(blockedId));
          setMessages((prev) => prev.filter((x) => x.user_id !== blockedId));
        }}
      />

      <NearbySheet
        open={nearbyOpen}
        onClose={() => setNearbyOpen(false)}
        onOpenProfile={(u) => { setProfileUser(u); setNearbyOpen(false); }}
      />

      <MaskSheet
        open={maskOpen}
        onClose={() => setMaskOpen(false)}
        roomId={id}
        currentMask={myMask}
        onUpdate={(m) => setMyMask(m || { enabled: false })}
      />
    </div>
  );
}

function ExpiryBanner({ expiresAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const target = new Date(expiresAt).getTime();
  const diffMs = target - now;
  if (diffMs <= 0) {
    return (
      <div className="px-4 py-2 text-center text-[11px] uppercase tracking-widest text-rose-300 bg-rose-500/10 border-b border-rose-500/30" data-testid="room-expired-banner">
        This room has ended.
      </div>
    );
  }
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const target_label = new Date(expiresAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return (
    <div className="flex items-center justify-center gap-2 px-4 py-2 text-[11px] uppercase tracking-widest text-amber-200 bg-amber-500/10 border-b border-amber-500/30" data-testid="room-expiry-banner">
      <Hourglass className="w-3 h-3" />
      <span>
        <span className="font-bold text-amber-100">{days}d {hours}h</span> until this room ends · {target_label}
      </span>
    </div>
  );
}

